import { create } from 'zustand'
import type { DeviceInfo, DevicePolicy, DeviceStatus, Me } from '../api/types'

/** What AuthGate renders for a signed-in user. */
export type GateView = 'app' | 'checking' | 'pending' | 'blocked'

export const UNBOUND_DEVICE: DeviceInfo = {
  status: 'unbound',
  id: null,
  code: null,
  name: null,
  requestedAt: null,
  approvedAt: null,
}

/**
 * Pure gate decision. Only "enforce" ever gates; "audit" and "off" always show the app.
 * `attesting` covers the first round trip after sign-in so an approved device does not flash the
 * waiting screen.
 */
export function gateView(policy: DevicePolicy | null | undefined, status: DeviceStatus | null | undefined, attesting = false): GateView {
  if (policy !== 'enforce') return 'app'
  switch (status) {
    case 'approved':
      return 'app'
    case 'pending':
      return 'pending'
    case 'denied':
    case 'revoked':
    case 'expired':
      return 'blocked'
    default:
      return attesting ? 'checking' : 'pending'
  }
}

interface DeviceGateState {
  policy: DevicePolicy
  device: DeviceInfo
  helpText: string | null
  /** An attestation round trip is running. */
  attesting: boolean
  /** A manual or automatic status check is running (for the "Checking" indicator). */
  checking: boolean
  lastCheckedAt: number | null
  /** Last error from attestation or a status check, shown on the gate screens. */
  error: string | null
  setFromMe: (me: Me) => void
  setDevice: (device: DeviceInfo) => void
  setAttesting: (attesting: boolean) => void
  setChecking: (checking: boolean, error?: string | null) => void
  reset: () => void
}

const initial = {
  policy: 'off' as DevicePolicy,
  device: UNBOUND_DEVICE,
  helpText: null,
  attesting: false,
  checking: false,
  lastCheckedAt: null,
  error: null,
}

export const useDeviceGate = create<DeviceGateState>((set) => ({
  ...initial,
  setFromMe: (me) =>
    set({
      // Tolerate a cached Me from before device approval existed.
      policy: me.devicePolicy ?? 'off',
      device: me.device ?? UNBOUND_DEVICE,
      helpText: me.deviceHelpText ?? null,
    }),
  setDevice: (device) => set({ device, error: null }),
  setAttesting: (attesting) => set({ attesting }),
  setChecking: (checking, error) =>
    set((s) => ({
      checking,
      lastCheckedAt: checking ? s.lastCheckedAt : Date.now(),
      error: error === undefined ? s.error : error,
    })),
  reset: () => set(initial),
}))

export function selectGateView(s: Pick<DeviceGateState, 'policy' | 'device' | 'attesting'>): GateView {
  return gateView(s.policy, s.device.status, s.attesting)
}

export function currentGateView(): GateView {
  return selectGateView(useDeviceGate.getState())
}

/** True when the app may connect the hub, sync and show cached data. */
export function deviceAllowed(): boolean {
  return currentGateView() === 'app'
}

type RecheckHandler = () => void
let recheckHandler: RecheckHandler = () => {}

export function setDeviceRecheckHandler(handler: RecheckHandler): void {
  recheckHandler = handler
}

/**
 * The server rejected a request with 403 device_not_approved: move to the gate and let the
 * lifecycle stop realtime and start polling.
 */
export function reportDeviceNotApproved(deviceStatus: DeviceStatus | undefined, code: string | null | undefined): void {
  const state = useDeviceGate.getState()
  const status: DeviceStatus = deviceStatus && deviceStatus !== 'approved' ? deviceStatus : 'pending'
  useDeviceGate.setState({
    // The server only sends this in enforce mode, whatever the cached policy says.
    policy: 'enforce',
    device: { ...state.device, status, code: code ?? state.device.code },
  })
  recheckHandler()
}
